import { Descriptions, Divider, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type FiservPaymentModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/FiservPaymentModuleWithPermissionsSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import FiservPaymentModuleActions from './FiservPaymentModuleActions';
import FiservPaymentSettingsEmptyList from './FiservPaymentSettingsEmptyList';
import FiservPaymentSettingsTable from './FiservPaymentSettingsTable';

export type FiservPaymentModulePageProps = { module: FiservPaymentModuleWithPermissionsSpecsFragment };

const FiservPaymentModulePage = ({ module }: FiservPaymentModulePageProps) => {
    const { t } = useTranslation(['fiservPaymentModuleDetails']);
    const navigate = useNavigate();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });
    const screens = Grid.useBreakpoint();

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('fiservPaymentModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('fiservPaymentModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<FiservPaymentModuleActions module={module} permissions={permissions} />}
            onBack={() => navigate('/admin/system/modules')}
            title={
                screens.md ? t('fiservPaymentModuleDetails:title', { name: module.displayName }) : module.displayName
            }
        >
            <Divider>{t('fiservPaymentModuleDetails:dividers.mainDetails')}</Divider>
            {module.paymentSettings.length === 0 ? (
                <FiservPaymentSettingsEmptyList module={module} />
            ) : (
                <FiservPaymentSettingsTable module={module} />
            )}
        </ConsolePageWithHeader>
    );
};

export default FiservPaymentModulePage;
